import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Radio,
  Button,
  Collapse,
  IconButton,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Paper,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClearIcon from '@mui/icons-material/Clear';
import PropTypes from 'prop-types';
import { environment } from '../../../environments/environment';

const RedeemPointItem = ({
  id,
  title,
  subtitle,
  points,
  saveText,
  selected,
  onSelect,
  balance,
  isFreeItem,
  productDetails,
  selectedItemId,
  onItemSelect,
  setFreeItemDetailsData,
  setFreeItemId,
}) => {
  const canAfford = balance >= points;
  const [localSelectedItemId, setLocalSelectedItemId] = useState(
    selectedItemId || ''
  );
  const handleRadioSelect = () => {
    if (canAfford && onSelect) {
      // For free items, allow selection even without item selected initially
      // The item selection will be enforced later when applying the reward
      onSelect(
        id,
        isFreeItem && localSelectedItemId ? localSelectedItemId : null
      );
      // If a free item is selected, propagate its relatedItemData up
      if (
        setFreeItemDetailsData &&
        isFreeItem &&
        localSelectedItemId &&
        productDetails &&
        productDetails.length
      ) {
        const found = productDetails.find(
          (it) =>
            String(it.id) === String(localSelectedItemId) ||
            String(it.itemId) === String(localSelectedItemId)
        );
        setFreeItemDetailsData(found || null);
        if (setFreeItemId) {
          setFreeItemId(found?.itemId || found?.id || null);
        }
      }
    }
  };

  const handleItemSelection = (event) => {
    const itemId = event.target.value;
    setLocalSelectedItemId(itemId);
    if (onItemSelect) {
      onItemSelect(id, itemId);
    }
    // If this reward is already selected, update the selection with the new item
    if (selected && onSelect) {
      onSelect(id, itemId);
    }
    // Propagate relatedItemData for the selected item up to parent if setter provided
    if (setFreeItemDetailsData && productDetails && productDetails.length) {
      const found = productDetails.find(
        (it) =>
          String(it.id) === String(itemId) ||
          String(it.itemId) === String(itemId)
      );
      setFreeItemDetailsData(found || null);
      if (setFreeItemId) {
        setFreeItemId(found?.itemId || found?.id || null);
      }
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: canAfford ? '#fff' : '#f5f5f5',
        borderRadius: '10px',
        padding: '22px 23px',
        opacity: canAfford ? 1 : 0.6,
      }}
    >
      <Box
        onClick={handleRadioSelect}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
          cursor: canAfford ? 'pointer' : 'not-allowed',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Radio
            checked={selected}
            disabled={!canAfford}
            onChange={handleRadioSelect}
            sx={{ alignSelf: 'flex-start', mt: { xs: 0.2, sm: 0 } }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '16px', sm: '18px' },
                fontFamily: 'Jost',
                color: canAfford ? '#172B4D' : '#999',
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {title}
              {isFreeItem && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 600,
                    color: '#28a745',
                    fontStyle: 'italic',
                    marginLeft: 1,
                  }}
                >
                  (Free Item - Select Below)
                </Typography>
              )}
              {!canAfford && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: '12px', sm: '14px' },
                    fontWeight: 400,
                    color: '#f44336',
                    fontStyle: 'italic',
                    marginLeft: 1,
                  }}
                >
                  (Insufficient points)
                </Typography>
              )}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: '#8C8C8C',
                  fontSize: { xs: '14px', sm: '16px' },
                  fontFamily: 'Jost',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </div>
        </Stack>

        <Stack
          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}
        >
          <Button
            size="small"
            variant="contained"
            sx={{
              backgroundColor: environment.mainColor,
              textTransform: 'none',
              borderRadius: '6px',
              padding: { xs: '6px 8px', sm: '6px 10px' },
              boxShadow: 'none',
              fontWeight: 600,
              fontSize: { xs: '13px', sm: '14px' },
              fontFamily: 'Jost',
              width: { xs: 'auto', sm: 'auto' },
              color: '#fff',
              '&:hover': {
                backgroundColor: environment.mainColor,
                boxShadow: '0 3px 8px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {`${points} Points`}
          </Button>
          {saveText && (
            <Typography
              variant="caption"
              sx={{
                color: '#08A451',
                marginTop: 0.5,
                fontSize: { xs: '12px', sm: '14px' },
                fontFamily: 'Jost',
                textAlign: { xs: 'left', sm: 'right' },
              }}
            >
              {saveText}
            </Typography>
          )}
        </Stack>
      </Box>

      {/* Item Selection for Free Items */}
      {(() => {
        const shouldShowSelection =
          isFreeItem && selected && productDetails && productDetails.length > 0;

        return shouldShowSelection;
      })() && (
        <Box sx={{ mt: 3, pl: 2, pr: 2 }}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: '#f8f9fa',
              border: `1px solid ${environment.mainColor}20`,
              borderRadius: '12px',
              padding: 3,
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  fontFamily: 'Jost',
                  color: '#2c3e50',
                  mb: 1,
                }}
              >
                <span role="img" aria-label="gift">
                  🎁
                </span>{' '}
                Choose your free item:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '14px',
                  fontFamily: 'Jost',
                  color: '#7f8c8d',
                }}
              >
                Select one item to get for free with this reward
              </Typography>
            </Box>

            {/* Radio Button Options */}
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={localSelectedItemId}
                onChange={handleItemSelection}
                sx={{ gap: 1 }}
              >
                {productDetails.map((item, index) => (
                  <Paper
                    key={item.id}
                    elevation={
                      localSelectedItemId === item.itemId ||
                      localSelectedItemId === item.id
                        ? 2
                        : 0
                    }
                    sx={{
                      border: `2px solid ${
                        localSelectedItemId === item.itemId ||
                        localSelectedItemId === item.id
                          ? environment.mainColor
                          : '#e9ecef'
                      }`,
                      borderRadius: '10px',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor:
                        localSelectedItemId === item.itemId ||
                        localSelectedItemId === item.id
                          ? `${environment.mainColor}08`
                          : '#fff',
                      '&:hover': {
                        borderColor: environment.mainColor,
                        backgroundColor: `${environment.mainColor}05`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${environment.mainColor}20`,
                      },
                    }}
                  >
                    <FormControlLabel
                      value={item.id}
                      control={
                        <Radio
                          sx={{
                            color: environment.mainColor,
                            '&.Mui-checked': {
                              color: environment.mainColor,
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: 20,
                            },
                          }}
                        />
                      }
                      label={
                        <Box sx={{ py: 1, width: '100%' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                              {/* Main Item Name */}
                              {item.relatedItemData?.name && (
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    fontFamily: 'Jost',
                                    color: '#2c3e50',
                                    lineHeight: 1.3,
                                    mb: 0.5,
                                  }}
                                >
                                  {item.relatedItemData.name}
                                </Typography>
                              )}

                              {/* Variation Name */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    fontFamily: 'Jost',
                                    color: '#5a6c7d',
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {item.name}
                                </Typography>

                                {item.type && (
                                  <Chip
                                    label={item.type.replace('_', ' ')}
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '10px',
                                      fontFamily: 'Jost',
                                      fontWeight: 500,
                                      backgroundColor: '#e9ecef',
                                      color: '#6c757d',
                                      textTransform: 'capitalize',
                                      '& .MuiChip-label': {
                                        px: 0.75,
                                        py: 0,
                                      },
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* Selected Badge */}
                            {(localSelectedItemId === item.itemId ||
                              localSelectedItemId === item.id) && (
                              <Chip
                                label="Selected"
                                size="small"
                                sx={{
                                  backgroundColor: environment.mainColor,
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  height: '22px',
                                  flexShrink: 0,
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                  },
                                }}
                              />
                            )}
                          </Box>

                          {/* Item Description (if available) */}
                          {item.relatedItemData?.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '12px',
                                fontFamily: 'Jost',
                                color: '#95a5a6',
                                lineHeight: 1.4,
                                mt: 0.5,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {item.relatedItemData.description}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{
                        margin: 0,
                        padding: '12px 16px',
                        width: '100%',
                        alignItems: 'flex-start',
                        '& .MuiFormControlLabel-label': {
                          width: '100%',
                          ml: 1,
                        },
                      }}
                    />
                  </Paper>
                ))}
              </RadioGroup>
            </FormControl>

            {/* Selection Status */}
            {!localSelectedItemId && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '13px',
                    fontFamily: 'Jost',
                    color: '#856404',
                    fontWeight: 500,
                  }}
                >
                  <span role="img" aria-label="warning">
                    ⚠️
                  </span>{' '}
                  Please select an item to proceed with this free reward
                </Typography>
              </Box>
            )}

            {localSelectedItemId && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '13px',
                    fontFamily: 'Jost',
                    color: '#155724',
                    fontWeight: 500,
                  }}
                >
                  <span role="img" aria-label="checkmark">
                    ✅
                  </span>{' '}
                  Great! Your free item has been selected
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
};

RedeemPointItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  points: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  saveText: PropTypes.string,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  balance: PropTypes.number,
  isFreeItem: PropTypes.bool,
  productDetails: PropTypes.array,
  selectedItemId: PropTypes.string,
  onItemSelect: PropTypes.func,
  setFreeItemDetailsData: PropTypes.func,
  setFreeItemId: PropTypes.func,
};

const RedeemPoints = ({
  balance,
  offers,
  selectedOfferId,
  onSelectOffer,
  onClearReward,
  defaultOpen = false,
  apiPricingData,
  setApiPricingData,
  onCalculatePrice,
  setFreeItemDetailsData,
  setFreeItemId,
  loyaltyAccountId = '832fe29b-3700-4977-ad33-be88f6aa9a54',
  storeId = '',
  deliveryOrPickup = 'pickup',
  deliveryId = '',
  totalAmount = 0,
  tipAmount = 0,
  currentCartData = {}, // Current cart data from Redux to preserve existing values
}) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));

  const [selectedItems, setSelectedItems] = useState({});

  const isFreeItemReward = (offer) => {
    const isFree = offer?.discount?.discountData?.percentage === '100.0';

    return isFree;
  };

  const handleSelectOffer = async (offerId, selectedItemId = null) => {
    // Check if user has enough balance for this reward
    const selectedOffer = offers.find((offer) => offer.id === offerId);
    if (selectedOffer && selectedOffer.points > balance) {
      return; // Don't proceed with selection
    }

    // For free items, allow initial selection but don't call calculatePrice until item is selected
    const isFreeItemWithoutSelection =
      isFreeItemReward(selectedOffer) && !selectedItemId;

    // Call the original onSelectOffer if provided
    if (onSelectOffer) {
      onSelectOffer(offerId, selectedItemId);
    }

    // Call the shared price calculation function from parent
    // Skip for free items that don't have an item selected yet
    if (onCalculatePrice && !isFreeItemWithoutSelection) {
      try {
        await onCalculatePrice(offerId, loyaltyAccountId, selectedItemId);
      } catch (error) {
        // Error handled silently
      }
    }
  };

  const handleItemSelect = async (offerId, itemId) => {
    setSelectedItems((prev) => ({
      ...prev,
      [offerId]: itemId,
    }));

    // If this is a free item reward and an item was selected, trigger price calculation
    const selectedOffer = offers.find((offer) => offer.id === offerId);
    if (isFreeItemReward(selectedOffer) && itemId && onCalculatePrice) {
      try {
        await onCalculatePrice(offerId, loyaltyAccountId, itemId);
      } catch (error) {
        // Error handled silently
      }
    }
  };

  return (
    <Box
      sx={{ marginBottom: 2, backgroundColor: '#FAFAFA', borderRadius: '8px' }}
    >
      <Box
        sx={{
          padding: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          cursor: 'pointer',
          gap: { xs: 1.5, sm: 0 },
        }}
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v);
        }}
      >
        <div>
          <Typography
            component="p"
            variant="h6"
            style={{
              fontWeight: '700',
              color: '#172B4D',
              fontSize: '20px',
              fontFamily: 'Jost',
            }}
          >
            Redeem Points
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#4A4A4A',
              marginTop: 0.5,
              fontSize: '18px',
              fontFamily: 'Jost',
            }}
          >
            Current balance:{' '}
            <span
              style={{
                color: environment.mainColor,
                fontWeight: 700,
                fontSize: '20px',
                fontFamily: 'Jost',
              }}
            >
              {balance} points
            </span>
          </Typography>
        </div>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 1, sm: 0 } }}
        >
          {selectedOfferId && onClearReward && (
            <Button
              size="small"
              variant="contained"
              startIcon={<ClearIcon sx={{ fontSize: '16px' }} />}
              onClick={(e) => {
                // keep click from toggling the collapse when tapping the button on mobile
                e.stopPropagation();
                onClearReward();
              }}
              sx={{
                backgroundColor: '#ff5722',
                color: 'white',
                fontFamily: 'Jost',
                fontWeight: 600,
                fontSize: { xs: '14px', sm: '13px' },
                padding: { xs: '5px 12px', sm: '7px 14px' },
                width: { xs: '100%', sm: 'auto' },
                minWidth: 'auto',
                borderRadius: '24px',
                textTransform: 'none',
                border: '2px solid transparent',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: '#e64a19',
                },
                '&:active': {
                  transform: 'translateY(-1px) scale(1.01)',
                },
                '&:focus': {
                  outline: 'none',
                  boxShadow: '0 0 0 3px rgba(255, 87, 34, 0.18)',
                },
              }}
            >
              Clear Reward
            </Button>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: environment.mainColor,
                fontWeight: 700,
                fontSize: { xs: '15px', sm: '16px' },
                fontFamily: 'Jost',
              }}
            >
              {open ? 'Hide' : 'Show'}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => !v);
              }}
              aria-label={
                open ? 'Collapse redeem points' : 'Expand redeem points'
              }
              sx={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 200ms',
                color: environment.mainColor,
                ml: 0,
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Stack>
      </Box>

      <Collapse in={open} timeout="auto">
        <Stack spacing={2} sx={{ padding: 2 }}>
          {offers && offers.length ? (
            offers.map((o) => {
              return (
                <RedeemPointItem
                  key={o.id}
                  id={o.id}
                  title={o.title}
                  subtitle={o.subtitle}
                  points={o.points}
                  saveText={o.saveText}
                  selected={selectedOfferId === o.id}
                  onSelect={handleSelectOffer}
                  balance={balance}
                  isFreeItem={isFreeItemReward(o)}
                  productDetails={o.productDetails || []}
                  selectedItemId={selectedItems[o.id] || ''}
                  onItemSelect={handleItemSelect}
                  setFreeItemDetailsData={setFreeItemDetailsData}
                  setFreeItemId={setFreeItemId}
                />
              );
            })
          ) : (
            <Typography variant="body2" sx={{ color: '#666' }}>
              No redeem offers available
            </Typography>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
};

RedeemPoints.propTypes = {
  balance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  offers: PropTypes.array,
  selectedOfferId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectOffer: PropTypes.func,
  onClearReward: PropTypes.func,
  onCalculatePrice: PropTypes.func,
  setFreeItemDetailsData: PropTypes.func,
  setFreeItemId: PropTypes.func,
  loyaltyAccountId: PropTypes.string,
  storeId: PropTypes.string,
  orderType: PropTypes.string,
  deliveryId: PropTypes.string,
  apiPricingData: PropTypes.object,
  setApiPricingData: PropTypes.func,
  deliveryOrPickup: PropTypes.string,
  totalAmount: PropTypes.number,
  tipAmount: PropTypes.number,
  currentCartData: PropTypes.object,
};

RedeemPoints.defaultProps = {
  balance: 0,
  offers: [],
  selectedOfferId: null,
  onSelectOffer: function noop() {
    return undefined;
  },
  onClearReward: null,
  onCalculatePrice: null,
  setFreeItemDetailsData: null,
  setFreeItemId: null,
  loyaltyAccountId: '832fe29b-3700-4977-ad33-be88f6aa9a54',
  storeId: '',
  orderType: 'pickup',
  deliveryId: '',
  apiPricingData: null,
  setApiPricingData: null,
  deliveryOrPickup: 'pickup',
  totalAmount: 0,
  tipAmount: 0,
  currentCartData: {},
};

export default RedeemPoints;
